import React from 'react';
import { useToast } from '@common/toast';
import LoginModal from '@components/loginModal';
import ResumeSaveModal from '@components/position/jd/apply/modals/resumeSaveModal';
import TermsModal from '@components/profile/modals/terms';
import PositionCancelModal from './PositionCancelModal';
import BookmarkFirstModal from './BookmarkFirstModal';
import BookmarkModal from './BookmarkModal';
import ProfileErrorModal from './ProfileErrorModal';
import ApplyExceedModal from './ApplyExceedModal';
import ProfileApplyModal from './ProfileApplyModal';
import PreviewProfileModal from './previewProfileModal';
import ApplySuccessModal from './ApplySuccessModal';
import ProfileRequiredFieldsModal from './profileRequiredFieldsModal';
import AdditionalInputModal from './additionalInputModal';
import { AdditionInfoFormProvider } from './additionalInputModal/AdditionalInputContext';
import { PositionDetailVO } from '@domain/vo/position/jd/PositionDetailVO';
import { PositionApplyCancelErrorCode } from '@domain/rs/position/jd/PositionAppyRs';
import { JobApplicationProcessType } from '@domain/constant/jobApplicationProcessType';
import { useKeyDown } from '@utils/hooks/useKeyboard';

export type JDModalType =
  | 'APPLY_EXCEED'
  | 'CANCEL'
  | 'LOGIN'
  | 'ERROR'
  | 'BOOKMARK_FIRST'
  | 'BOOKMARK'
  | 'RESUME_SAVE'
  | 'EASY_APPLY'
  | 'PREVIEW_PROFILE'
  | 'APPLY_SUCCESS'
  | 'PROFILE_REQUIRED'
  | 'ADDITIONAL_INPUT'
  | 'TERMS'
  | null;

interface JDModalsProps {
  positionSn?: number;
  modalState: JDModalType;
  setModalState: (modalState: JDModalType) => void;
  setApplyState: (applyState: JobApplicationProcessType | null) => void;
  positionDetail: PositionDetailVO;
  onTermsConfirm: () => Promise<void>;
}

const JDModals = ({
  positionSn,
  modalState,
  setModalState,
  setApplyState,
  positionDetail,
  onTermsConfirm,
}: JDModalsProps) => {
  const Toast = useToast();

  useKeyDown(() => {
    switch (modalState) {
      case 'EASY_APPLY':
      case 'APPLY_SUCCESS':
      case 'CANCEL':
        handleClose();
        break;

      case 'PREVIEW_PROFILE':
        setModalState('EASY_APPLY');
        break;
    }
  }, 'Escape');

  if (!positionSn) return null;

  const isVisible = (modalType: JDModalType) => modalType === modalState;
  const handleClose = () => setModalState(null);
  const handleCloseResumeSaveModal = () => setModalState('CANCEL');

  const handleCancelApplyError = (code: PositionApplyCancelErrorCode) => {
    let content: string;
    switch (code) {
      case 'B901':
        content = '채용 전형이 종료된 포지션 입니다.';
        break;
      case 'B902':
        content = '지원한 이력이 없는 포지션 입니다.';
        break;
      case 'B903':
        content = '이미 채용 전형이 진행중인 포지션 입니다.';
        break;
      case 'B904':
        content = '마감된 포지션 입니다.';
        break;
      case 'B905':
        content = '삭제된 포지션 입니다.';
        break;
      case 'B908':
      default:
        //ATS 서버와 통신에 실패했습니다.
        return setModalState('ERROR');
    }

    Toast({
      type: 'error',
      iconType: 'info',
      content,
    });
    handleClose();
  };

  return (
    // 추가 정보 입력 모달 전역 Context
    <AdditionInfoFormProvider>
      {/* [D] 입사 지원 횟수 초과 */}
      <ApplyExceedModal visible={isVisible('APPLY_EXCEED')} onClose={handleClose} positionSn={positionSn} />

      {/* [D] 입사 취소 */}
      <PositionCancelModal
        positionSn={positionSn}
        visible={isVisible('CANCEL')}
        onClose={handleClose}
        onError={handleCancelApplyError}
        setApplyState={setApplyState}
      />

      {/* [D] 첫 북마크 */}
      <BookmarkFirstModal visible={isVisible('BOOKMARK_FIRST')} onClose={handleClose} />

      {/* [D] 북마크 */}
      <BookmarkModal visible={isVisible('BOOKMARK')} onClose={handleClose} />

      {/* [D] 통신 에러 */}
      <ProfileErrorModal visible={isVisible('ERROR')} onClose={handleClose} />

      {/* [D] 지원서 잡다에 저장  */}
      <ResumeSaveModal
        positionSn={positionSn}
        visible={isVisible('RESUME_SAVE')}
        onClose={handleCloseResumeSaveModal}
      />

      {/* 프로필 지원 */}
      <ProfileApplyModal
        visible={isVisible('EASY_APPLY')}
        onClose={handleClose}
        setModalState={setModalState}
        positionSn={positionSn}
      />

      {/* 프로필 미리보기 */}
      <PreviewProfileModal visible={isVisible('PREVIEW_PROFILE')} onClose={() => setModalState('EASY_APPLY')} />

      {/* 프로필 지원 제출 완료 */}
      <ApplySuccessModal visible={isVisible('APPLY_SUCCESS')} onClose={handleClose} positionDetail={positionDetail} />

      {/* 프로필 필수 입력사항 */}
      <ProfileRequiredFieldsModal
        visible={isVisible('PROFILE_REQUIRED')}
        onClose={handleClose}
        positionSn={positionSn}
        modalState={modalState}
        setModalState={setModalState}
      />

      {/* 프로필 지원 추가 정보 입력 모달 */}
      <AdditionalInputModal
        visible={isVisible('ADDITIONAL_INPUT')}
        setModalState={setModalState}
        positionSn={positionSn}
      />

      {/* 동의문 모달 */}
      <TermsModal visible={isVisible('TERMS')} onClose={handleClose} onConfirm={onTermsConfirm} />

      {/* [D] 로그인 */}
      <LoginModal
        subTitle="채용정보 · 역량검사 · 매칭까지"
        title="취업기회 지금 바로 잡다!"
        visible={isVisible('LOGIN')}
        onClose={handleClose}
      />
    </AdditionInfoFormProvider>
  );
};

export default JDModals;
